import React, { useState } from "react";
import { FiLoader } from "react-icons/fi";
import { CiEdit } from "react-icons/ci";
import { MdBlock, MdOutlinePublishedWithChanges } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
  activeUser,
  deactiveUser,
} from "../../../../Store/Slices/Admin/Users/Staff/staff.action";
import DeleteConfirmatiomModal from "../../../../Components/Common/DeleteConfirmationModal";
import { motion } from "framer-motion";
import profileIcon from "../../../../Assets/DashboardAssets/profileIcon.png";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../../config/permission";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaChalkboardTeacher } from "react-icons/fa";
import { Tooltip } from "antd";

const ProfileCard = ({ profile, onClick, editUser }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((store) => store.admin.all_staff);
  const role = useSelector((store) => store.common.auth.role);
  const MYId = useSelector((store) => store.common.user?.userDetails?.userId);
  const { t } = useTranslation("admUserProfile");
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNameTooltipVisible, setIsNameTooltipVisible] = useState(false);
  const [isPositionTooltipVisible, setIsPositionTooltipVisible] = useState(false);

  const adminStaffRole = role === "admin" || profile?.role === role;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const deleteTeacher = () => {
    const userData = { id: profile._id, role: profile.role };
    dispatch(deactiveUser(userData));
    setIsModalOpen(false);
  };

  const activateUser = (e, id, role) => {
    e.stopPropagation();
    const userData = { staffId: id, role, active: true };
    dispatch(activeUser(userData));
  };

  const getContact = () => profile.contactNumber || profile.mobileNumber;

  const getDisplayName = () =>
    profile.fullName ||
    `${profile.firstName || ""} ${profile.lastName || ""}`.trim();

  const isUserActive = () => {
    if (typeof profile.active === "boolean") return profile.active;
    if (typeof profile.status === "string")
      return profile.status.toLowerCase() === "active";
    return false;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="relative w-full col-span-1 "
    >
      <div
        onClick={() => onClick(profile)}
        className=" group relative bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col h-full"
      >
        {/* ── Gradient cover band ────────────────────────── */}
        <div className="relative h-24 bg-gray-50 overflow-hidden flex-shrink-0">
          {/* Subtle pattern overlay */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        {/* ── Status badge ── */}
        <div className="absolute top-3 left-4 z-20">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${
              isUserActive()
                ? "bg-white/90 text-green-600 border border-green-200"
                : "bg-white/90 text-red-500 border border-red-200"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isUserActive() ? "bg-green-500 animate-pulse" : "bg-red-400"}`} />
            {isUserActive() ? t("Active") : t("Inactive")}
          </span>
        </div>

        {/* ── Floating action buttons (hover) ── */}
        {adminStaffRole && (
          <div className="absolute top-3 right-4 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
            {isUserActive() && (
              <ProtectedAction requiredPermission={PERMISSIONS[`EDIT_${profile?.role.toUpperCase()}`]}>
                {MYId != profile._id && (
                  <Tooltip title={t("Edit Profile")} placement="left">
                    <button
                      onClick={(e) => editUser(e, profile)}
                      className="p-1.5 bg-white rounded-full shadow hover:bg-indigo-500 hover:text-white transition-all duration-200 text-slate-500"
                    >
                      {loading ? (
                        <FiLoader className="animate-spin w-3.5 h-3.5" />
                      ) : (
                        <CiEdit className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </Tooltip>
                )}
              </ProtectedAction>
            )}

            {profile?.role === "teacher" && (
              <Tooltip title={t("Assign to Class")} placement="left">
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/class/assign/${profile?._id}`); }}
                  className="p-1.5 bg-white rounded-full shadow hover:bg-purple-500 hover:text-white transition-all duration-200 text-slate-500"
                >
                  <FaChalkboardTeacher className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}

            {isUserActive() ? (
              <ProtectedAction requiredPermission={PERMISSIONS[`DEACTIVE_${profile?.role.toUpperCase()}`]}>
                {MYId != profile._id && (
                  <Tooltip title={t("Deactivate")} placement="left">
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(); }}
                      className="p-1.5 bg-white rounded-full shadow hover:bg-red-500 hover:text-white transition-all duration-200 text-slate-500"
                    >
                      <MdBlock className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                )}
              </ProtectedAction>
            ) : (
              <ProtectedAction requiredPermission={PERMISSIONS[`ACTIVE_${profile?.role.toUpperCase()}`]}>
                {MYId != profile._id && (
                  <Tooltip title={t("Activate")} placement="left">
                    <button
                      onClick={(e) => activateUser(e, profile._id, profile.role)}
                      className="p-1.5 bg-white rounded-full shadow hover:bg-green-500 hover:text-white transition-all duration-200 text-slate-500"
                    >
                      <MdOutlinePublishedWithChanges className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                )}
              </ProtectedAction>
            )}
          </div>
        )}

        {/* ── Avatar — overlapping the cover band ── */}
        <div className="relative flex flex-col items-center -mt-12 z-10 px-6 pb-6">
          <div className="relative group-hover:scale-105 transition-transform duration-500">
            {/* Glowing ring on hover */}
            <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-violet-400 to-pink-400 opacity-0 group-hover:opacity-60 blur-sm transition-opacity duration-500" />
            <img
              className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl z-10"
              src={profile?.profile || profileIcon}
              alt={getDisplayName()}
            />
          </div>

          {/* Role pill under avatar */}
          <span className="mt-2.5 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-300  shadow-sm">
            {profile?.role || "—"}
          </span>

          {/* Name */}
          <h3 className="mt-2 text-base font-bold text-gray-900 text-center leading-snug truncate w-full  transition-colors duration-300">
            {getDisplayName()}
          </h3>

          {/* Position */}
          {role === "admin" && (
            <p className="text-xs text-gray-400 font-medium mt-1 text-center truncate w-full">
              {profile?.position?.join(", ") || t("No position")}
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="mt-auto border-t border-gray-100 px-6 py-3 flex items-center justify-between bg-gray-50 group-hover:bg-purple-50/40 transition-colors duration-500">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            {t("Contact")}
          </span>
          <span className="text-sm font-semibold text-gray-700 truncate max-w-[130px]">
            {getContact() || "—"}
          </span>
        </div>
      </div>

      {!loading && adminStaffRole && (
        <DeleteConfirmatiomModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onConfirm={deleteTeacher}
          text={t("Deactivate")}
        />
      )}
    </motion.div>
  );
};

export default ProfileCard;